import { isFromBreakpoint } from '~/breakpoints';
import { publish } from '~/event-bus';
import { HTMLCustomElement } from '~/html-custom-element';

customElements.define(
  'main-product',
  class MainProduct extends HTMLCustomElement {
    stickyPausedClass = 'main-product--sticky-form-paused';
    stickyFormTopProperty = '--sticky-form-top';

    connectedCallback() {
      if (super.connectedCallback) super.connectedCallback();

      this.on('variant', this.targets.form, (event) => {
        this.setQueryParam(event.detail);
        this.setVariantSection(event.detail);
      });

      this.on('scroll', window, () => this.handleScroll(), null);

      requestAnimationFrame(() => {
        this.handleScroll();
      });
    }

    /**
     * Handles the window scroll event.
     */
    handleScroll() {
      if (!isFromBreakpoint('large')) {
        return;
      }

      const gallery = this.querySelector('product-gallery');
      const fifthItem = gallery.targets.item && gallery.targets.item.all.at(4);

      if (!fifthItem) {
        return;
      }

      const offset = window.scrollY - (window.innerHeight - fifthItem.getBoundingClientRect().bottom);

      if (
        /**
         * Reset to default behaviour if:
         * - The form (and it's offset) is taller than the gallery OR
         * - The fifth gallery item is below the fold.
         */
        this.targets.form.targets.form.offsetHeight + offset >= gallery.targets.container.offsetHeight ||
        fifthItem.getBoundingClientRect().bottom >= window.innerHeight
      ) {
        this.enableProductFormSticky();
        return;
      }

      /**
       * Remove native sticky-ness and apply `top` for continuity.
       */
      this.disableProductFormSticky(offset);
    }

    /**
     * Pauses the product form sticky styling and applies `top` for continuity.
     * @param {Number} offset - The current scroll offset.
     */
    disableProductFormSticky(offset) {
      this.targets.container.classList.add(this.stickyPausedClass);

      if (!this.targets.container.style.getPropertyValue(this.stickyFormTopProperty)) {
        this.targets.container.style.setProperty(this.stickyFormTopProperty, `${offset}px`);
      }
    }

    /**
     * Sets the default sticky state of the product form.
     */
    enableProductFormSticky() {
      this.targets.container.classList.remove(this.stickyPausedClass);
      this.targets.container.style.removeProperty(this.stickyFormTopProperty);
    }

    /**
     * Fetches and returns the variant's section content.
     * @param {Object} variant - The variant object.
     * @returns {Promise}
     */
    fetchVariantSection(variant) {
      return fetch(`${window.location.pathname}?section_id=${this.dataset.sectionId}&variant=${variant.id}`).then(
        (response) => response.text()
      );
    }

    /**
     * Sets the variant section.
     * - Fetches the section content, and injects it.
     * @param {Object} variant - The variant object.
     */
    async setVariantSection(variant) {
      this.classList.add('is-loading');

      publish('product:update', {
        sections: {
          [this.dataset.sectionId]: await this.fetchVariantSection(variant),
        },
      });

      requestAnimationFrame(() => {
        this.classList.remove('is-loading');
      });
    }

    /**
     * Sets the variant query parameter.
     * @param {Object} variant - The variant object.
     */
    setQueryParam(variant) {
      const params = new URLSearchParams(window.location.search);

      params.set('variant', variant.id);

      window.history.replaceState(null, null, `?${params.toString()}`);
    }
  }
);
